import { useEffect, useRef } from 'react';

import { Table } from '@tanstack/react-table';

import { useHashDocumentId } from '@/modules/collections/hooks/useHashDocumentId';

// Generic type that can work with any data that has a document_id field
type DocumentLike = { document_id: string | null };

interface UseDocumentHighlightProps<TData extends DocumentLike> {
  table: Table<TData>;
  data: TData[];
  resourceName: string | undefined;
}

/**
 * Owns the deep-link side effect for the collections document tables: when the URL hash targets a
 * document (#document-<id>), jump to the page that holds it and scroll it into view.
 *
 * The reactive "which document is targeted" read lives in `useHashDocumentId` (consumed by the
 * parent to drive `activeRowId`); this hook reuses it for the target id and only performs the
 * imperative navigation, so the hash pattern is parsed in a single place.
 */
export function useDocumentHighlight<TData extends DocumentLike>({
  table,
  data,
  resourceName,
}: UseDocumentHighlightProps<TData>) {
  const documentId = useHashDocumentId();
  // The jump is a one-shot per hash target. `data` stays reactive so the effect retries until the
  // row has loaded, but this ref stops it re-firing on later mutations while the hash lingers.
  const handledIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      handledIdRef.current = null;
      return;
    }
    if (handledIdRef.current === documentId) {
      return;
    }

    // Find the document in the data array
    const documentIndex = data.findIndex((doc) => doc.document_id === documentId);
    if (documentIndex === -1) {
      return;
    }

    handledIdRef.current = documentId;

    // Navigate to the page that holds the document if we're not already there
    const pageSize = table.getState().pagination.pageSize;
    const targetPage = Math.floor(documentIndex / pageSize);
    if (targetPage !== table.getState().pagination.pageIndex) {
      table.setPageIndex(targetPage);
    }

    // Scroll to the document after a short delay to allow for page navigation. This targets the row
    // element id rendered by DataTable (`${resourceName}-row-<rowId>`), so callers must use a
    // getRowId that returns document_id for document rows — otherwise the scroll silently no-ops.
    const timeout = setTimeout(() => {
      const element = document.getElementById(`${resourceName}-row-${documentId}`);
      if (!element) {
        return;
      }

      // Find the table scroll container instead of using scrollIntoView - scrollIntoView scrolls
      // ALL ancestors including viewport, and we only want the table to scroll.
      const scrollContainer = element.closest('[data-slot="table-container"]');
      if (!scrollContainer) {
        return;
      }

      const elementRect = element.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Calculate position to center element within container
      const elementTop = elementRect.top - containerRect.top + scrollContainer.scrollTop;
      const centerOffset = (containerRect.height - elementRect.height) / 2;

      scrollContainer.scrollTo({ top: Math.max(0, elementTop - centerOffset), behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(timeout);
  }, [documentId, data, table, resourceName]);
}
